import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, TrendingUp, Scissors, ChevronDown } from 'lucide-react';

const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

const monthLabel = (ym) => {
  const d = new Date(ym + '-01T00:00:00');
  const s = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const dayMonth = (ymd) => {
  const [, m, d] = ymd.split('-');
  return `${d}/${m}`;
};

const colors = [
  'linear-gradient(135deg,#c9a96e,#a07840)',
  'linear-gradient(135deg,#9b59b6,#7d3c98)',
  'linear-gradient(135deg,#2d8c4e,#1e6b38)',
  'linear-gradient(135deg,#2980b9,#1a5276)',
  'linear-gradient(135deg,#e74c3c,#c0392b)',
];
const initials = (name) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

function StylistCard({ s, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="earnings-card" style={{ background: '#fff', border: '1px solid #f0e9df', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '1.3rem 1.4rem', textAlign: 'left' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1rem' }}>
          {s.photo ? (
            <img src={`/uploads/${s.photo}`} alt={s.name}
              style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover' }}
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
          ) : null}
          <div className="photo-initials" style={{ background: colors[idx % colors.length], width: 52, height: 52, borderRadius: '50%', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600, display: s.photo ? 'none' : 'flex' }}>
            {initials(s.name)}
          </div>
          <div style={{ flex: 1 }}>
            <strong style={{ display: 'block', fontSize: '1.02rem' }}>{s.name}</strong>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-gray)' }}>{s.total_bookings} atendimento{s.total_bookings === 1 ? '' : 's'}</span>
          </div>
          <ChevronDown size={20} style={{ color: 'var(--gold-dark)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
        <div style={{ borderTop: '1px solid #f0e9df', paddingTop: '0.9rem', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-gray)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Faturou no mês</span>
          <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#27ae60' }}>{brl(s.total_revenue)}</span>
        </div>
        {!open && s.total_bookings > 0 && (
          <span style={{ fontSize: '0.78rem', color: 'var(--gold-dark)', marginTop: '0.6rem', display: 'inline-block' }}>
            Clique para ver o que ela fez ▾
          </span>
        )}
      </button>

      {open && (
        <div style={{ borderTop: '1px solid #f0e9df', padding: '0.4rem 1.4rem 1.2rem' }}>
          {s.items.length === 0 ? (
            <p style={{ color: 'var(--text-gray)', fontSize: '0.85rem', padding: '0.8rem 0' }}>Nenhum atendimento neste mês.</p>
          ) : (
            <>
              {s.items.map((it, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.8rem', padding: '0.7rem 0', borderTop: i === 0 ? 'none' : '1px solid #f6f0e6' }}>
                  <div style={{ minWidth: 0 }}>
                    <strong style={{ fontSize: '0.9rem', display: 'block' }}>{it.service_name}</strong>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-gray)' }}>
                      {dayMonth(it.booking_date)} às {it.booking_time}
                      {it.client_name ? ` · ${it.client_name}` : ''}
                    </span>
                  </div>
                  <span style={{ fontWeight: 700, color: '#27ae60', whiteSpace: 'nowrap', fontSize: '0.9rem' }}>{brl(it.price)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', paddingTop: '0.8rem', marginTop: '0.3rem', borderTop: '2px solid var(--gold-light)' }}>
                <strong style={{ fontSize: '0.9rem' }}>Total de {s.name.split(' ')[0]}</strong>
                <strong style={{ color: '#27ae60', whiteSpace: 'nowrap' }}>{brl(s.total_revenue)}</strong>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminEarnings() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const pw = sessionStorage.getItem('admin_password');
    axios.get('/api/admin/earnings', { params: { month }, headers: { 'x-admin-password': pw } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 className="admin-section-title" style={{ marginBottom: 0 }}>
          Faturamento por Profissional <span style={{ fontSize: '1rem', color: 'var(--text-gray)', fontFamily: 'Poppins' }}>({monthLabel(month)})</span>
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            style={{ padding: '0.5rem 0.8rem', borderRadius: '10px', border: '1.5px solid var(--gold-light)', fontFamily: 'Poppins', color: 'var(--text-dark)' }}
          />
          <button className="btn-secondary" style={{ fontSize: '0.85rem', padding: '0.6rem 1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={load}>
            <RefreshCw size={15} /> Atualizar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">Carregando...</div>
      ) : !data || data.stylists.length === 0 ? (
        <div className="empty-state">
          <Scissors size={40} strokeWidth={1} style={{ color: 'var(--gold-light)', marginBottom: '0.75rem' }} />
          <p>Nenhuma profissional cadastrada.</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.2rem', marginBottom: '1.5rem' }}>
            {data.stylists.map((s, idx) => (
              <StylistCard key={s.id} s={s} idx={idx} />
            ))}
          </div>

          <div style={{ background: 'linear-gradient(135deg,#fbf7f0,#f6efe3)', border: '1px solid var(--gold-light)', borderRadius: '14px', padding: '1.1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>
              <TrendingUp size={18} /> Total do salão ({data.totalBookings} atendimento{data.totalBookings === 1 ? '' : 's'})
            </span>
            <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#27ae60' }}>{brl(data.total)}</span>
          </div>
        </>
      )}

      <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-gray)' }}>
        * Clique em uma profissional para ver o que ela fez no mês. Considera os agendamentos não cancelados, somando o preço de cada serviço.
      </p>
    </div>
  );
}
